import _ from "lodash";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { compose, graphql } from "lib/apolloClient";

import classes from "./TopHeader.scss";
import UserMenuButton from "AppComponents/UserMenuButton";
import I18nHOC from "UIComponents/I18nHOC";
import withPusherBind from "UIComponents/PusherConsumerHoc";
import { ToddleIcon } from "SvgComponents";

import { getNotificationsCountQuery } from "modules/CommonQuery";
import { goToPlatformHomeChildRoute } from "modules/NavigationModule";
import { setHomePageSidebar } from "Teacher/modules/TeacherModule";

import { getCategoryTabsForNotification } from "modules/Services";
import { IconButton, Badge } from "@toddle-edu/ds-web";
import {
  NotificationBellOutlined,
  HelpBulbOutlined,
  HelpBulbFilled,
  HeartLikeOutlined,
} from "@toddle-edu/ds-icons";

import ACLStore from "lib/aclStore";

import Lottie from "lottie-react";
import beatingHeartAnimation from "../Utils/heart-animation.json";
import { messageEnumsConsts } from "Courses/components/components/UserMessage/Utils/consts";
import { isMessagePermissioned } from "Courses/components/components/UserMessage/Utils/utils";
import { getCurriculumProgramFreeStatus } from "Platform/modules/PlatformModule";
import { checkIfShowNotificationInGlobalTrayEnabled } from "GlobalMenuTray/Utils";

const TopHeader = props => {
  const [isHeartAnimationComplete, setHeartAnimationStatus] = useState(true);

  useEffect(() => {
    if (isMessagePermissioned(messageEnumsConsts.VALENTINE_MESSAGE)) {
      setTimeout(() => {
        setHeartAnimationStatus(false);
      }, 1000);
    }
  }, []);

  const {
    logoutUser,
    t,
    setHomePageSidebar,
    notificationCount,
    goToPlatformHomeChildRoute,
    isCurrentAcademicYearSelected,
    contentType,
    onUserMessageClick,
    showNotificationIconInHeader,
  } = props;

  const onClickNotificationCenter = () => {
    setHomePageSidebar({
      showSidebar: true,
      contentType: "NOTIFICATION_CENTER",
    });
  };

  const onClickUserCommunicationMessage = key => {
    onUserMessageClick({ selectedUserMessageEnum: key });
  };

  const NotificationCount = () => {
    return notificationCount > 0 ? (
      <div className={classes.notificationCount}>
        <div className={classes.notificationCountContent}>
          {notificationCount}
        </div>
      </div>
    ) : null;
  };

  const onProfileSettingsClick = useCallback(
    () => goToPlatformHomeChildRoute({ route: "notification-preferences" }),
    []
  );

  return (
    <div className={classes.container}>
      <div className={classes.greetingComponent}>
        <div className={classes.toddleIcon}>
          <ToddleIcon />
        </div>
        <div className={classes.detailContainer}>
          <div className={classes.userName}>{t(`common:home`)}</div>
        </div>
      </div>
      <div className={classes.rightMenu}>
        <div className={classes.userMenuContainer}>
          <UserMenuButton
            onLogout={logoutUser}
            t={t}
            onProfileSettingsClick={onProfileSettingsClick}
          />
        </div>
        <div className={classes.menuButtonContainer}>
          {isCurrentAcademicYearSelected && (
            <div className={classes.iconGroup}>
              {isMessagePermissioned(messageEnumsConsts.VALENTINE_MESSAGE) ? (
                <div
                  className={classes.heartIcons}
                  onClick={() =>
                    onClickUserCommunicationMessage(
                      messageEnumsConsts.VALENTINE_MESSAGE
                    )
                  }
                >
                  {!isHeartAnimationComplete ? (
                    <Lottie
                      animationData={beatingHeartAnimation}
                      autoplay={true}
                      loop={false}
                      className={classes.heartAnimationLottie}
                      onComplete={() => setHeartAnimationStatus(true)}
                    />
                  ) : (
                    <HeartLikeOutlined
                      className={classes.heartSvg}
                      size="x-small"
                    />
                  )}
                </div>
              ) : null}
              {showNotificationIconInHeader ? (
                <div className={classes.NotificationBell}>
                  <IconButton
                    isActivated={contentType === "NOTIFICATION_CENTER"}
                    id="COACHMARKS_DASHBOARD_NOTIFICATION_CENTER"
                    icon={
                      <Badge
                        value={notificationCount}
                        type={"numeric"}
                        size={"medium"}
                        variant={"notifications"}
                      >
                        <NotificationBellOutlined />
                      </Badge>
                    }
                    onClick={onClickNotificationCenter}
                    variant="plain"
                    size="large"
                  />
                </div>
              ) : null}
              {ACLStore.can("FeatureFlag:ShowLearnAndInspire") && (
                <IconButton
                  isActivated={contentType === "TODDLE_ACADEMY"}
                  onClick={() => {
                    setHomePageSidebar({
                      showSidebar: true,
                      contentType: "TODDLE_ACADEMY",
                    });
                  }}
                  icon={
                    contentType === "TODDLE_ACADEMY" ? (
                      <HelpBulbFilled />
                    ) : (
                      <HelpBulbOutlined />
                    )
                  }
                  variant={"plain"}
                  size={"large"}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const userInfo = _.get(state, "login.userInfo", {});
  const isCurrentAcademicYearSelected = _.get(
    state,
    "platform.academicYearSelected.isCurrentAcademicYear",
    true
  );

  const categoryTabs = getCategoryTabsForNotification({
    userType: state.login.userInfo.user_type,
    isCurriculumProgramFree: getCurriculumProgramFreeStatus({ state }),
  });
  const showNotificationIconInHeader = !checkIfShowNotificationInGlobalTrayEnabled();

  return {
    userId: _.get(userInfo, "id", ""),
    userEntityType: _.get(userInfo, "userEntityType", ""),
    categoryTabs,
    isCurrentAcademicYearSelected,
    contentType: _.get(state, "teacher.sidebarConfig.contentType", null),
    showNotificationIconInHeader,
  };
};

const mapActionCreators = {
  setHomePageSidebar,
  goToPlatformHomeChildRoute,
};

export default compose(
  I18nHOC({ resource: ["common"] }),
  connect(mapStateToProps, mapActionCreators),
  graphql(getNotificationsCountQuery, {
    name: "getNotificationsCount",
    skip: ({ showNotificationIconInHeader }) => !showNotificationIconInHeader,
    options: ({ userId, userEntityType, categoryTabs }) => ({
      fetchPolicy: "cache-and-network",
      variables: {
        id: userId,
        type: userEntityType,
        notificationCategoryFilter: categoryTabs,
      },
      context: {
        useHttpLink: true,
      },
    }),
    props({ getNotificationsCount, ownProps: { isData, isLoading } }) {
      const notification = _.get(
        getNotificationsCount,
        "node.notification",
        {}
      );

      return {
        getNotificationsCount,
        onPusherEventTriggered: () => getNotificationsCount.refetch(),
        isData: !_.isEmpty(notification) && isData,
        notificationCount: _.get(notification, "newCount", 0),
        isLoading:
          getNotificationsCount["networkStatus"] == 1 ||
          getNotificationsCount["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }),
  withPusherBind(["notificationAdded"])
)(TopHeader);
